import type { BufferedItem, BufferedTrack, BufferedIdentify } from './types.js';

export interface PreInitBufferOptions {
  /** 'drop' = no-op; 'queue' = FIFO queue. In SSR (no window) always drop. */
  mode: 'drop' | 'queue';
  maxSize: number;
  ttlMs: number;
  /** For testing: override browser check so queue works in Node. */
  isBrowserOverride?: boolean;
}

export type FlushTrackHandler = (payload: { name: string; properties?: Record<string, unknown>; timestamp?: string }) => void;
export type FlushIdentifyHandler = (userId: string, traits?: Record<string, unknown>) => void;

/**
 * Pre-init buffer: either drops events or queues them (FIFO) until flush.
 * In Node/SSR (typeof window === 'undefined') always behaves as drop.
 */
export class PreInitBuffer {
  private readonly options: PreInitBufferOptions;
  private readonly queue: BufferedItem[] = [];
  private readonly isBrowser: boolean;

  constructor(options: PreInitBufferOptions) {
    this.options = options;
    this.isBrowser =
      options.isBrowserOverride !== undefined ? options.isBrowserOverride : typeof window !== 'undefined';
  }

  /**
   * Push a track call. No-op if mode is drop or not browser (SSR).
   */
  pushTrack(name: string, properties?: Record<string, unknown>, timestamp?: string): void {
    if (this.options.mode === 'drop' || !this.isBrowser) return;
    const item: BufferedTrack = {
      type: 'track',
      name,
      properties,
      timestamp,
      createdAt: Date.now(),
    };
    this.push(item);
  }

  /**
   * Push an identify call. No-op if mode is drop or not browser (SSR).
   */
  pushIdentify(userId: string, traits?: Record<string, unknown>): void {
    if (this.options.mode === 'drop' || !this.isBrowser) return;
    const item: BufferedIdentify = {
      type: 'identify',
      userId,
      traits,
      createdAt: Date.now(),
    };
    this.push(item);
  }

  private push(item: BufferedItem): void {
    while (this.queue.length >= this.options.maxSize && this.queue.length > 0) {
      this.queue.shift(); // drop oldest (FIFO)
    }
    this.queue.push(item);
  }

  /**
   * Replay queued items: all identify first (order), then all track (order).
   * Items older than ttlMs are dropped. Then clear the queue.
   */
  flush(
    onTrack: FlushTrackHandler,
    onIdentify: FlushIdentifyHandler
  ): void {
    if (!this.isBrowser || this.queue.length === 0) {
      this.queue.length = 0;
      return;
    }
    const now = Date.now();
    const cutoff = now - this.options.ttlMs;
    const identifyItems = this.queue.filter(
      (item): item is BufferedIdentify => item.type === 'identify' && item.createdAt >= cutoff
    );
    const trackItems = this.queue.filter(
      (item): item is BufferedTrack => item.type === 'track' && item.createdAt >= cutoff
    );
    for (const item of identifyItems) {
      onIdentify(item.userId, item.traits);
    }
    for (const item of trackItems) {
      onTrack({
        name: item.name,
        properties: item.properties,
        timestamp: item.timestamp,
      });
    }
    this.queue.length = 0;
  }

  /** Number of items currently queued (for tests). */
  get length(): number {
    return this.queue.length;
  }
}
